import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CircleSlash, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarDataHora } from '@/lib/utils';
import { useAuth } from '@/features/auth/contexto';
import type { SolicitacaoDetalhe } from '@/features/solicitacoes/tipos';
import { ehResponsavelDaUbs, nomesDosResponsaveis } from '@/features/solicitacoes/responsaveis';
import {
  podeDecidirCancelamento,
  podeSolicitarCancelamento,
} from '@/features/cancelamentos/alcada';
import { Botao } from '@/components/ui/button';
import { AreaTexto } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

/** Tamanho mínimo do texto livre, igual ao exigido pelo backend. */
const MINIMO = 10;

interface Props {
  solicitacao: SolicitacaoDetalhe;
}

/**
 * Cancelamento — pedido da UBS e decisão de quem tem alçada.
 *
 * Um componente só para os dois lados porque é uma conversa só: o que a UBS
 * escreve no pedido é exatamente o que a Regulação lê antes de decidir, e
 * separar isso em duas telas faria o motivo se perder no caminho.
 */
export function PainelCancelamento({ solicitacao }: Props) {
  const { usuario } = useAuth();
  const queryClient = useQueryClient();
  const [acao, setAcao] = React.useState<'pedir' | 'confirmar' | 'recusar' | null>(null);

  if (!usuario) return null;

  const pedidoAberto = Boolean(solicitacao.cancelamentoSolicitadoEm);
  const ehUbs = usuario.perfil === 'UBS';

  const invalidar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['solicitacao', solicitacao.id] });
    await queryClient.invalidateQueries({ queryKey: ['historico', solicitacao.id] });
    await queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
    await queryClient.invalidateQueries({ queryKey: ['estatisticas'] });
  };

  const fechar = () => setAcao(null);

  // --- Lado da UBS ----------------------------------------------------------
  if (ehUbs) {
    if (!pedidoAberto && !podeSolicitarCancelamento(solicitacao.status)) return null;

    return (
      <>
        <Cartao>
          <CartaoCabecalho>
            <CartaoTitulo>Cancelamento</CartaoTitulo>
            <CartaoDescricao>
              {pedidoAberto
                ? 'Pedido registrado. A Regulação decide se o cancelamento se confirma.'
                : 'Use quando o paciente avisar que não poderá comparecer — a vaga volta para outra pessoa.'}
            </CartaoDescricao>
          </CartaoCabecalho>
          <CartaoConteudo>
            {pedidoAberto ? (
              <Resumo solicitacao={solicitacao} />
            ) : (
              <Botao variante="contorno" onClick={() => setAcao('pedir')}>
                <CircleSlash aria-hidden="true" />
                Solicitar cancelamento
              </Botao>
            )}
          </CartaoConteudo>
        </Cartao>

        <DialogoPedido
          aberto={acao === 'pedir'}
          aoFechar={fechar}
          solicitacaoId={solicitacao.id}
          agendada={solicitacao.status === 'AGENDADO'}
          aoConcluir={invalidar}
        />
      </>
    );
  }

  // --- Lado de quem decide --------------------------------------------------
  if (!pedidoAberto) return null;

  const temAlcada = podeDecidirCancelamento(usuario.perfil, solicitacao.status);
  // A Regulação segue a mesma regra das demais decisões: só os responsáveis.
  const ehResponsavel =
    usuario.perfil !== 'REGULACAO' || ehResponsavelDaUbs(solicitacao.ubs, usuario.id);

  return (
    <>
      <Cartao className="border-status-cancelado-borda">
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <CircleSlash className="size-4" aria-hidden="true" />
            Cancelamento pedido pela UBS
          </CartaoTitulo>
          <CartaoDescricao>
            Enquanto não for decidido, o horário continua preso a quem já avisou que não vai
            comparecer.
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo className="flex flex-col gap-3">
          <Resumo solicitacao={solicitacao} />

          {temAlcada && ehResponsavel ? (
            <div className="flex flex-wrap gap-2">
              <Botao variante="destrutivo" onClick={() => setAcao('confirmar')}>
                <CircleSlash aria-hidden="true" />
                Confirmar cancelamento
              </Botao>
              <Botao variante="contorno" onClick={() => setAcao('recusar')}>
                Recusar pedido
              </Botao>
            </div>
          ) : (
            <p className="text-silenciado-texto flex items-start gap-2 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                {!temAlcada ? (
                  <>
                    Esta solicitação já está <strong>agendada</strong>: o cancelamento desmarca um
                    atendimento e devolve a vaga, e só a Regulação pode confirmá-lo.
                  </>
                ) : (
                  <>
                    As solicitações da UBS <strong>{solicitacao.ubs.nome}</strong> são reguladas por{' '}
                    <strong>{nomesDosResponsaveis(solicitacao.ubs)}</strong>. Você pode
                    acompanhá-las, mas não registrar decisões.
                  </>
                )}
              </span>
            </p>
          )}
        </CartaoConteudo>
      </Cartao>

      <DialogoConfirmar
        aberto={acao === 'confirmar'}
        aoFechar={fechar}
        solicitacaoId={solicitacao.id}
        agendada={solicitacao.status === 'AGENDADO'}
        aoConcluir={invalidar}
      />
      <DialogoRecusar
        aberto={acao === 'recusar'}
        aoFechar={fechar}
        solicitacaoId={solicitacao.id}
        aoConcluir={invalidar}
      />
    </>
  );
}

/** Motivo, autor e data do pedido — o que sustenta a decisão. */
function Resumo({ solicitacao }: Props) {
  return (
    <div className="text-sm">
      <p className="whitespace-pre-line">{solicitacao.cancelamentoMotivo}</p>
      <p className="text-silenciado-texto mt-1 text-xs">
        Pedido por {solicitacao.cancelamentoSolicitadoPor?.nome ?? 'usuário da UBS'} em{' '}
        {formatarDataHora(solicitacao.cancelamentoSolicitadoEm)}
      </p>
    </div>
  );
}

interface DialogoProps {
  aberto: boolean;
  aoFechar: () => void;
  solicitacaoId: string;
  aoConcluir: () => Promise<void>;
}

/** Hook comum aos três diálogos: envia, avisa, atualiza e fecha. */
function useAcao(
  caminho: string,
  mensagemSucesso: string,
  { aoConcluir, aoFechar }: Pick<DialogoProps, 'aoConcluir' | 'aoFechar'>,
) {
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) => api.post(caminho, corpo),
    onSuccess: async () => {
      toast.success(mensagemSucesso);
      await aoConcluir();
      aoFechar();
    },
    onError: (erro) => {
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível registrar o pedido.',
      );
    },
  });
}

function DialogoPedido({
  aberto,
  aoFechar,
  solicitacaoId,
  agendada,
  aoConcluir,
}: DialogoProps & { agendada: boolean }) {
  const [motivo, setMotivo] = React.useState('');
  const pedir = useAcao(
    `/solicitacoes/${solicitacaoId}/cancelamento`,
    'Pedido registrado. A Regulação será avisada ao abrir a solicitação.',
    { aoConcluir, aoFechar },
  );

  const curto = motivo.trim().length > 0 && motivo.trim().length < MINIMO;

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Solicitar cancelamento</DialogoTitulo>
          <DialogoDescricao>
            {agendada
              ? 'O atendimento está agendado: só a Regulação confirma o cancelamento, porque é ela quem devolve a vaga no eSUS.'
              : 'O pedido vai para a Regulação ou a gestão, que confirma ou recusa. A solicitação continua como está até lá.'}
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo
            id="motivo-cancelamento"
            rotulo="Motivo do cancelamento"
            obrigatorio
            erro={curto ? 'Descreva o motivo com pelo menos 10 caracteres.' : undefined}
          >
            <AreaTexto
              rows={4}
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: Paciente informou que estará fora da cidade na data e pediu para liberar a vaga."
            />
          </Campo>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Voltar
          </Botao>
          <Botao
            onClick={() => pedir.mutate({ motivo: motivo.trim() })}
            disabled={pedir.isPending || motivo.trim().length < MINIMO}
          >
            {pedir.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Enviar pedido
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

function DialogoConfirmar({
  aberto,
  aoFechar,
  solicitacaoId,
  agendada,
  aoConcluir,
}: DialogoProps & { agendada: boolean }) {
  const [observacao, setObservacao] = React.useState('');
  const confirmar = useAcao(
    `/solicitacoes/${solicitacaoId}/cancelamento/confirmar`,
    'Cancelamento confirmado.',
    { aoConcluir, aoFechar },
  );

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Confirmar cancelamento</DialogoTitulo>
          <DialogoDescricao>
            {agendada
              ? 'A solicitação será encerrada e paciente e UBS receberão o aviso por e-mail. Lembre-se de liberar a vaga no eSUS: o KAVI não cancela do lado de lá.'
              : 'A solicitação será encerrada. Nenhum aviso é enviado ao paciente — ele não chegou a ser chamado para atendimento.'}
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo id="observacao-cancelamento" rotulo="Observação (opcional)">
            <AreaTexto
              rows={3}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex.: Vaga devolvida à fila da especialidade."
            />
          </Campo>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Voltar
          </Botao>
          <Botao
            variante="destrutivo"
            onClick={() =>
              confirmar.mutate(observacao.trim() ? { observacao: observacao.trim() } : {})
            }
            disabled={confirmar.isPending}
          >
            {confirmar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Confirmar cancelamento
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

function DialogoRecusar({ aberto, aoFechar, solicitacaoId, aoConcluir }: DialogoProps) {
  const [justificativa, setJustificativa] = React.useState('');
  const recusar = useAcao(
    `/solicitacoes/${solicitacaoId}/cancelamento/recusar`,
    'Pedido recusado. A solicitação seguiu como estava.',
    { aoConcluir, aoFechar },
  );

  const curto = justificativa.trim().length > 0 && justificativa.trim().length < MINIMO;

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Recusar pedido de cancelamento</DialogoTitulo>
          <DialogoDescricao>
            A solicitação continua no status atual. A UBS lê a justificativa no histórico.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo
            id="justificativa-recusa"
            rotulo="Por que o pedido não foi acatado"
            obrigatorio
            erro={curto ? 'Explique a recusa com pelo menos 10 caracteres.' : undefined}
          >
            <AreaTexto
              rows={3}
              autoFocus
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Ex.: Paciente confirmou presença por telefone nesta data."
            />
          </Campo>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Voltar
          </Botao>
          <Botao
            onClick={() => recusar.mutate({ justificativa: justificativa.trim() })}
            disabled={recusar.isPending || justificativa.trim().length < MINIMO}
          >
            {recusar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Recusar pedido
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}
